import { ArrowAllCalendars } from 'assets/icons'
import { MenuAccount, MenuPrint } from 'assets/icons/menu'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccountDoc, UserSettingsDoc } from 'types/api/changes'
import { Model } from 'types/model'

import AccountActionsPopup from './AccountActionsPopup'

export interface AccountSectionProps {
  userSettings?: Model<UserSettingsDoc>
  account?: Model<AccountDoc>
  setOpen: (b: boolean) => void
}

export default function AccountSection(props: AccountSectionProps) {
  const { t, i18n } = useTranslation()

  const [showAccount, setShowAccount] = useState(false)

  function performPrint(): void {
    props.setOpen(false)
    setTimeout(() => {
      type LegacyMediaQueryList = MediaQueryList & {
        addListener?: (listener: (event: MediaQueryListEvent) => void) => void
        removeListener?: (
          listener: (event: MediaQueryListEvent) => void
        ) => void
      }

      let didCleanup = false
      let fallbackTimeoutId = 0
      const printMediaQuery =
        typeof window.matchMedia === 'function'
          ? (window.matchMedia('print') as LegacyMediaQueryList)
          : null

      const cleanupPrintMode = () => {
        if (didCleanup) return
        didCleanup = true

        document.body.classList.remove('print-calendar-only')
        window.dispatchEvent(new Event('resize'))
        window.requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'))
        })
        window.removeEventListener('afterprint', cleanupPrintMode)
        window.removeEventListener('focus', cleanupPrintMode)
        document.removeEventListener('visibilitychange', onVisibilityChange)

        if (printMediaQuery) {
          if (printMediaQuery.removeEventListener) {
            printMediaQuery.removeEventListener('change', onPrintMediaChange)
          } else if (printMediaQuery.removeListener) {
            printMediaQuery.removeListener(onPrintMediaChange)
          }
        }

        window.clearTimeout(fallbackTimeoutId)
      }

      const onPrintMediaChange = (event: MediaQueryListEvent) => {
        if (!event.matches) cleanupPrintMode()
      }

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') cleanupPrintMode()
      }

      document.body.classList.add('print-calendar-only')
      window.addEventListener('afterprint', cleanupPrintMode)
      window.addEventListener('focus', cleanupPrintMode, { once: true })
      document.addEventListener('visibilitychange', onVisibilityChange)

      if (printMediaQuery) {
        if (printMediaQuery.addEventListener) {
          printMediaQuery.addEventListener('change', onPrintMediaChange)
        } else if (printMediaQuery.addListener) {
          printMediaQuery.addListener(onPrintMediaChange)
        }
      }

      fallbackTimeoutId = window.setTimeout(cleanupPrintMode, 60000)

      try {
        window.print()
      } catch {
        cleanupPrintMode()
      }
    }, 300)
  }

  return (
    <PopupSection popupSectionGap={1}>
      <PopupSectionRow
        actionBg
        action={performPrint}
        iconClass="icon-regular"
        maxWidthIcon={20}
        engulfEntireRow
        icon={<MenuPrint />}
        title={t('print')}
      />
      <AccountActionsPopup
        account={props.account}
        userSettings={props.userSettings}
        setOpen={setShowAccount}
        open={showAccount}
      >
        <span>
          <PopupSectionRow
            title={t('account')}
            icon={<MenuAccount />}
            iconClass="icon-regular"
            maxWidthIcon={20}
            actionId="account-menu-button"
            actionBg
            engulfEntireRow
            value={
              <ArrowAllCalendars
                style={{
                  transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)',
                  cursor: 'pointer',
                  marginTop: 4
                }}
              />
            }
            action={() => {
              setShowAccount(true)
            }}
          />
        </span>
      </AccountActionsPopup>
    </PopupSection>
  )
}
